import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v2Unscoped, v2api } from './v2'
import { useSessionStore } from '@/stores/sessionStore'


export const configKeys = {
  all: ['config'] as const,
  pos: () => [...configKeys.all, 'pos'] as const,
  sysConfig: (keys: string[]) => [...configKeys.all, 'sys', keys] as const,
}


/**
 * What happens after a sale tenders out. Mirrors the BE enum
 * `PosReceiptCompleteAction` exposed via `pos.receipt.complete_action`:
 *   - 'ask'     → show clerk a Print Receipt / Done choice (default).
 *   - 'print'   → auto-print, show only Done.
 *   - 'nothing' → no auto-print, show only Done.
 */
export type ReceiptCompleteAction = 'ask' | 'print' | 'nothing'

export interface POSConfig {
  isMarijuana: boolean
  pointsEnabled: boolean
  posAutoAdd: boolean
  posCustomerRequired: boolean
  posDefaultCustomerId: number | null
  posDiscountsDisabled: boolean
  posDiscountsShow: boolean
  posScaleEnabled: boolean
  posUseSerialNumber: boolean
  posPricesBeforeTaxes: boolean
  /**
   * When true, scanning the same product twice bumps qty on one line.
   * When false (default), each scan gets its own row. Mirrors the BE
   * `pos.receipt.group_items` SysConfig (read inside ops_receipt.add_items).
   * The FE must match BE behavior so the optimistic cart shape matches what
   * /cart/commit will persist.
   */
  posGroupItems: boolean
  posReceiptCompleteAction: ReceiptCompleteAction
  /**
   * Seconds of inactivity before the POS routes back to the PIN view
   * (`/login`). 0 / null disables the auto-logout. Driven by the
   * `pos.logout_timeout` SysConfig set in waio-portal.
   */
  posLogoutTimeout: number | null
  /**
   * When true, lot/batch tracking is "load-bearing" for cannabis lines —
   * scanned GS1 lots are validated against the product's batch list, and
   * cannabis lines without a lot trigger a confirm-and-pay step at
   * checkout. When false the lot is purely informational. Mirrors the
   * waio-portal `frontend.show_available_batches` SysConfig (V1's
   * `posShowAvailableBatches`); enable on cannabis stores, leave off
   * elsewhere.
   */
  posShowAvailableBatches: boolean
  /** `frontend.hide_out_of_stock`: the store hides inactive / flagged
   *  out-of-stock products from search. The till (waio-portal
   *  ops_receipt.add_item) refuses those products under the same
   *  setting, and ONLY under it. Three places mirror that: the route's
   *  handleAddToCart (toasts), posStore.addToCart (silent backstop, via
   *  getPOSConfigSnapshot) and OrderDetailModal's "buy again" — so a
   *  scan hears "no" up front instead of at /cart/commit. */
  posHideOutOfStock: boolean
  /**
   * When true, loading a hold/ecom order into the POS requires the cashier
   * to re-scan each cannabis line to confirm its lot before checkout — the
   * scan stamps the physically-scanned batch onto the line and payment is
   * hard-blocked until every line is confirmed. Mirrors the waio-portal
   * `frontend.on_hold_scan_to_confirm_lot` SysConfig (V1's
   * `posOnHoldScanToConfirmLot`). V1 applied it to held orders only; V2
   * extends it to ecom orders too.
   */
  posOnHoldScanToConfirmLot: boolean
  /**
   * When true, the cashier can't add more units of a SKU/lot than its
   * on-hand count — both the product modal's qty `+` and the cart row's
   * qty `+` cap out. When false, oversell is permitted (IOH may be
   * stale, transfers in-flight, etc.). Mirrors the waio-portal
   * `pos.products.verify_if_is_over_on_hand` SysConfig.
   */
  posVerifyIfIsOverOnHand: boolean
  /**
   * When true, percent SpringBig redemptions apply to the receipt's
   * grand_total (incl. tax/deposits). When false (default), they apply
   * to the subtotal only. Mirrors the BE branch at frontend.py:600,
   * which reads `pos.apply_rewards_discounts_on_final_total`. The FE
   * uses this to keep its display estimate of the loyalty reduction in
   * sync with the BE's authoritative computation.
   */
  posApplyRewardsDiscountsOnFinalTotal: boolean
  /**
   * When true, a manual cashier discount may not price a line below its
   * cost. Mirrors the waio-portal `pos.disallow_price_lower_than_cost`
   * SysConfig (default on). Enforced client-side in ProductModal: the
   * cashier can't apply a per-line discount that drops the unit price
   * under the product's cost.
   */
  posDisallowPriceLowerThanCost: boolean
  /**
   * When true, the store accepts delivery orders ("Allow Delivery" in the
   * online shop setup). Gates the POS Orders → Deliveries tab and its order
   * fetch. Mirrors the waio-portal `kiosk.delivery.allow_delivery` SysConfig
   * (the same key the shop/POS reads to enable delivery ordering).
   */
  posAllowDelivery: boolean
  /**
   * When true, hitting Checkout raises a blocking "did you check for two
   * pieces of ID?" confirmation before the tender modal opens. Mirrors the
   * waio-portal `pos.confirmation_of_age_limit` SysConfig, toggled at
   * Frontend → Setup → "Confirmation of age limit".
   *
   * STRICT opt-in: only an explicit `true` arms the prompt. The field
   * declares `initial=True`, but that only seeds the portal's form widget —
   * it never reaches this endpoint. `/sys_config?keys=` maps every key to
   * `default=None` (hypnotoad/handlers/api/sys_config.py from_dict), and
   * `_SysConfig._read` returns that default for a missing or null-valued
   * row, so an untouched flag arrives here as `null`. Treating `null` as
   * "on" would pop the modal while the portal toggle plainly reads off.
   *
   * This is a deliberate divergence from V1, which fetched the key on the
   * single-key path with `default=true` and therefore prompted even when
   * the toggle looked off. The toggle is now the single source of truth.
   *
   * Only takes effect at stores that actually have an age restriction set
   * (`Store.restriction_age`) — see useAgeRestriction.
   */
  posConfirmationOfAgeLimit: boolean
}


// Last POSConfig this session resolved, mirrored outside React so
// non-component code can read the store's merchandising flags.
// posStore.addToCart is the consumer: it is a plain zustand action (no
// hooks, no queryClient in scope) and has to apply the same till gate
// the route handler does for the callers that bypass the handler.
//
// Written from an effect rather than the queryFn because the queryFn
// often does not run at all -- the query is `offlineFirst` with a 24h
// gcTime and is persisted to IndexedDB, so a register that reopens with
// a fresh cache serves `data` without a fetch. The effect fires for
// hydrated data too.
let posConfigSnapshot: POSConfig | null = null

/**
 * Non-reactive read of the last resolved POSConfig. `null` means "not
 * loaded yet", which callers must treat as unknown rather than off --
 * every gate built on it should fail OPEN, matching what the server
 * would do for a store whose SysConfig is unset.
 */
export function getPOSConfigSnapshot(): POSConfig | null {
  return posConfigSnapshot
}


export function usePOSConfig() {
  const { selectedStore } = useSessionStore()

  const query = useQuery({
    queryKey: configKeys.pos(),
    queryFn: async (): Promise<POSConfig> => {
      // Every key the POSConfig below reads MUST be listed here: the
      // portal's /sys_config answers with the requested keys only, so a
      // key missing from this string is permanently `undefined` and its
      // flag permanently false -- which silently disables whatever gate
      // depends on it (frontend.hide_out_of_stock was omitted and the
      // merchandising gate never fired).
      const keys = 'system.is_marijuana,system.points.enabled,frontend.auto_add_product,frontend.customer_required,frontend.default_customer_id,pos.discounts.disabled,pos.discounts.show,pos.scale.enabled,frontend.use_serial_number,pos.price_before_taxes,pos.receipt.group_items,pos.receipt.complete_action,pos.logout_timeout,frontend.show_available_batches,frontend.hide_out_of_stock,pos.products.verify_if_is_over_on_hand,pos.apply_rewards_discounts_on_final_total,pos.disallow_price_lower_than_cost,frontend.on_hold_scan_to_confirm_lot,kiosk.delivery.allow_delivery,pos.confirmation_of_age_limit'

      const data = await v2api.get<Record<string, unknown>>(v2Unscoped('/sys_config'),{ keys })

      const rawCompleteAction = data['pos.receipt.complete_action']
      const completeAction: ReceiptCompleteAction =
        rawCompleteAction === 'print' || rawCompleteAction === 'nothing'
          ? rawCompleteAction
          : 'ask'

      // BE quirk: waio-portal's "Idle timeout, seconds" field is labeled
      // seconds but stored as milliseconds (the save handler multiplies
      // by 1000). We divide here so the rest of the FE works in seconds
      // — matches the field label, sibling settings (jwt_expiry_hours,
      // jwt_refresh_days), and what cashiers expect to type. Mirror this
      // conversion in SettingsModal display. If the portal is fixed,
      // remove BOTH conversions.
      const rawLogoutTimeout = data['pos.logout_timeout']
      const logoutTimeoutMs =
        typeof rawLogoutTimeout === 'number'
          ? rawLogoutTimeout
          : typeof rawLogoutTimeout === 'string' && rawLogoutTimeout.trim() !== ''
            ? Number(rawLogoutTimeout)
            : null
      const logoutTimeout =
        Number.isFinite(logoutTimeoutMs) && logoutTimeoutMs !== null
          ? Math.round(logoutTimeoutMs / 1000)
          : null

      return {
        isMarijuana: data['system.is_marijuana'] === true,
        pointsEnabled: data['system.points.enabled'] === true,
        posAutoAdd: data['frontend.auto_add_product'] === true,
        posCustomerRequired: data['frontend.customer_required'] === true,
        posDefaultCustomerId: data['frontend.default_customer_id'] as number | null,
        posDiscountsDisabled: data['pos.discounts.disabled'] === true,
        posDiscountsShow: data['pos.discounts.show'] !== false,
        posScaleEnabled: data['pos.scale.enabled'] === true,
        posUseSerialNumber: data['frontend.use_serial_number'] === true,
        posPricesBeforeTaxes: data['pos.price_before_taxes'] === true,
        posGroupItems: data['pos.receipt.group_items'] === true,
        posReceiptCompleteAction: completeAction,
        posLogoutTimeout: logoutTimeout,
        posShowAvailableBatches: data['frontend.show_available_batches'] === true,
        posHideOutOfStock: data['frontend.hide_out_of_stock'] === true,
        // Default off — only explicit `true` arms the scan-to-confirm gate.
        posOnHoldScanToConfirmLot: data['frontend.on_hold_scan_to_confirm_lot'] === true,
        // Default to verifying (true) when the SysConfig is unset — safer
        // to block oversell by default; stores that need oversell flip
        // the flag to false in waio-portal.
        posVerifyIfIsOverOnHand: data['pos.products.verify_if_is_over_on_hand'] !== false,
        // Default to false (BE applies % to subtotal) when the SysConfig
        // is unset — matches the BE default at frontend.py:600.
        posApplyRewardsDiscountsOnFinalTotal: data['pos.apply_rewards_discounts_on_final_total'] === true,
        // Default ON when unset (matches the BE field's initial=True);
        // only an explicit `false` turns it off.
        posDisallowPriceLowerThanCost: data['pos.disallow_price_lower_than_cost'] !== false,
        // Default OFF when unset — a store must explicitly enable delivery in
        // the online shop setup before the POS surfaces the Deliveries tab.
        posAllowDelivery: data['kiosk.delivery.allow_delivery'] === true,
        // Strict opt-in — an untouched flag comes back as `null` (see the
        // interface comment), and `null` must mean off so the POS matches
        // what the portal toggle shows.
        posConfirmationOfAgeLimit: data['pos.confirmation_of_age_limit'] === true,
      }
    },
    enabled: !!selectedStore?.id,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })

  useEffect(() => {
    if (query.data) posConfigSnapshot = query.data
  }, [query.data])

  return query
}


export function useSysConfig(keys: string[]) {
  return useQuery({
    queryKey: configKeys.sysConfig(keys),
    queryFn: async (): Promise<Record<string, unknown>> => {
      return v2api.get<Record<string, unknown>>(v2Unscoped('/sys_config'),{
        keys: keys.join(','),
      })
    },
    enabled: keys.length > 0,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60 * 24,
    networkMode: 'offlineFirst',
  })
}


export function useDefaultCustomerId() {
  const { data: config } = usePOSConfig()
  return config?.posDefaultCustomerId ?? null
}
